import { InventoryTransferItems } from '@/collections/inventory_transfer_items';
import { InventoryTransferItemModel, InventoryTransferItemAPIInterface } from './inventory_transfer_item';

export interface InventoryTransferItemInterface {
  productVariantId: number;
  quantity: number;
  productName?: string;
  sku?: string;
}

// Transfer creation interfaces
export interface CreateTransferItemRequest {
  product_variant_id: number;
  quantity: number;
  note?: string;
  // Priority information
  priority_score?: number;
  stockout_risk_score?: number;
  revenue_impact_score?: number;
  lead_time_urgency_score?: number;
  // Product information
  product_id?: number;
  product_title?: string;
  variant_title?: string;
  sku?: string;
  image_url?: string;
  unit_cost?: number;
}

export interface CreateTransferRequest {
  source_location_id: number;
  destination_location_id: number;
  note?: string;
  items: CreateTransferItemRequest[];
  // Transfer-level priority
  priority_score?: number;
  priority_level?: string;
}

export interface TransferItemAPIResponse {
  id: number;
  inventory_transfer_id: number;
  product_variant_id: number;
  quantity: number;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTransferAPIResponse {
  id: number;
  source_location_id: number;
  destination_location_id: number;
  status: string;
  created_by: string;
  note: string | null;
  created_at: string;
  updated_at: string;
  items: TransferItemAPIResponse[];
}

export type TransferStatus = 'PENDING' | 'SHIPPED' | 'PARTIAL_RECEIVED' | 'COMPLETED' | 'CANCELLED';
export type ReceiveMode = 'full' | 'custom';
export type PriorityLevel = 'Low' | 'Normal' | 'High' | 'Critical';

export interface InventoryTransferInterface {
  id: number;
  sourceLocId: number;
  destLocId: number;
  status: TransferStatus;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  shippedAt?: string;
  receivedAt?: string;
  note?: string;
  items?: InventoryTransferItemInterface[];
  sourceLocationName?: string;
  destinationLocationName?: string;
  // Priority scoring properties
  priorityScore?: number;
  stockoutRiskScore?: number;
  revenueImpactScore?: number;
  leadTimeUrgencyScore?: number;
  priorityLevel?: PriorityLevel;
  // Partial receive fields
  receiveMode?: ReceiveMode;
  isPartialReceive?: boolean;
  totalExpectedQty?: number;
  totalReceivedQty?: number;
  forceCompleted?: boolean;
  forceCompleteReason?: string;
  forceCompleteAt?: string;
  // Lead time for transit (from operations center)
  leadTimeDays?: number;
}

export class InventoryTransferModel {
  public id: number;
  public sourceLocId: number;
  public destLocId: number;
  public status: TransferStatus;
  public createdBy: string;
  public createdAt: string;
  public updatedAt: string;
  public shippedAt: string;
  public receivedAt: string;
  public note: string;
  public items: InventoryTransferItems;
  public sourceLocationName: string;
  public destinationLocationName: string;
  // Priority scoring properties
  public priorityScore: number;
  public stockoutRiskScore: number;
  public revenueImpactScore: number;
  public leadTimeUrgencyScore: number;
  public priorityLevel: PriorityLevel;
  // Partial receive fields
  public receiveMode: ReceiveMode;
  public isPartialReceive: boolean;
  public totalExpectedQty: number;
  public totalReceivedQty: number;
  public forceCompleted: boolean;
  public forceCompleteReason: string;
  public forceCompleteAt: string;
  public leadTimeDays: number;

  constructor(data?: Partial<InventoryTransferModel>) {
    this.id = data?.id ?? 0;
    this.sourceLocId = data?.sourceLocId ?? 0;
    this.destLocId = data?.destLocId ?? 0;
    this.status = data?.status ?? 'PENDING';
    this.createdBy = data?.createdBy ?? '';
    this.createdAt = data?.createdAt ?? '';
    this.updatedAt = data?.updatedAt ?? '';
    this.shippedAt = data?.shippedAt ?? '';
    this.receivedAt = data?.receivedAt ?? '';
    this.note = data?.note ?? '';
    this.items = data?.items ?? new InventoryTransferItems();
    this.sourceLocationName = data?.sourceLocationName ?? '';
    this.destinationLocationName = data?.destinationLocationName ?? '';
    // Priority scoring properties
    this.priorityScore = data?.priorityScore ?? 0;
    this.stockoutRiskScore = data?.stockoutRiskScore ?? 0;
    this.revenueImpactScore = data?.revenueImpactScore ?? 0;
    this.leadTimeUrgencyScore = data?.leadTimeUrgencyScore ?? 0;
    this.priorityLevel = data?.priorityLevel ?? 'Normal';
    // Partial receive fields
    this.receiveMode = data?.receiveMode ?? 'full';
    this.isPartialReceive = data?.isPartialReceive ?? false;
    this.totalExpectedQty = data?.totalExpectedQty ?? 0;
    this.totalReceivedQty = data?.totalReceivedQty ?? 0;
    this.forceCompleted = data?.forceCompleted ?? false;
    this.forceCompleteReason = data?.forceCompleteReason ?? '';
    this.forceCompleteAt = data?.forceCompleteAt ?? '';
    this.leadTimeDays = data?.leadTimeDays ?? 5;
  }

  public mapData(data: any): this {
    // Ensure ID is converted to number
    this.id = Number(data.id || 0);

    // Ensure location IDs are numbers
    this.sourceLocId = Number(data.source_loc_id || data.source_location_id || 0);
    this.destLocId = Number(data.dest_loc_id || data.destination_location_id || 0);

    // Ensure strings are properly assigned
    this.status = (data.status || 'PENDING') as TransferStatus;
    this.createdBy = String(data.created_by || 'USER');
    this.createdAt = String(data.created_at || '');
    this.updatedAt = String(data.updated_at || '');
    this.shippedAt = String(data.shipped_at || '');
    this.receivedAt = String(data.received_at || '');
    this.note = String(data.note || '');

    // Map items collection
    this.items = new InventoryTransferItems();
    if (data.items && Array.isArray(data.items)) {
      this.items.items = data.items.map((item: InventoryTransferItemAPIInterface) =>
        new InventoryTransferItemModel().mapData(item),
      );
      this.items.total = this.items.items.length;
    }

    // Handle location names with proper fallback
    this.sourceLocationName = String(data.source_location_name || '') || `Location ${this.sourceLocId}`;
    this.destinationLocationName = String(data.destination_location_name || '') || `Location ${this.destLocId}`;

    // Map priority properties
    this.priorityScore = Number(data.priority_score || 0);
    this.stockoutRiskScore = Number(data.stockout_risk_score || 0);
    this.revenueImpactScore = Number(data.revenue_impact_score || 0);
    this.leadTimeUrgencyScore = Number(data.lead_time_urgency_score || 0);
    this.priorityLevel = (data.priority_level || 'Normal') as PriorityLevel;

    // Map partial receive properties
    this.receiveMode = (data.receive_mode || 'full') as ReceiveMode;
    this.isPartialReceive = Boolean(data.is_partial_receive || false);
    this.totalExpectedQty = Number(data.total_expected_qty || 0);
    this.totalReceivedQty = Number(data.total_received_qty || 0);
    this.forceCompleted = Boolean(data.force_completed || false);
    this.forceCompleteReason = String(data.force_complete_reason || '');
    this.forceCompleteAt = String(data.force_complete_at || '');

    // Map lead time days from API
    this.leadTimeDays = Number(data.lead_time_days || 5);

    return this;
  }

  get statusColor(): string {
    switch (this.status) {
      case 'PENDING': return 'orange';
      case 'SHIPPED': return 'blue';
      case 'PARTIAL_RECEIVED': return 'amber';
      case 'COMPLETED': return 'green';
      case 'CANCELLED': return 'red';
      default: return 'grey';
    }
  }

  get statusIcon(): string {
    switch (this.status) {
      case 'PENDING': return 'mdi-clock-outline';
      case 'SHIPPED': return 'mdi-truck-delivery';
      case 'PARTIAL_RECEIVED': return 'mdi-package-variant';
      case 'COMPLETED': return 'mdi-check-circle';
      case 'CANCELLED': return 'mdi-cancel';
      default: return 'mdi-help-circle';
    }
  }

  get formattedCreatedAt(): string {
    return new Date(this.createdAt).toLocaleDateString();
  }

  get formattedUpdatedAt(): string {
    return new Date(this.updatedAt).toLocaleDateString();
  }

  get totalItems(): number {
    return this.items.items.reduce((sum, item) => sum + item.quantity, 0);
  }

  get totalVariants(): number {
    return this.items.items.length;
  }

  // Permission getters based on status
  get canEditItems(): boolean {
    return this.status === 'PENDING';
  }

  get canEditNote(): boolean {
    return this.status !== 'CANCELLED';
  }

  get canShip(): boolean {
    return this.status === 'PENDING';
  }

  get canReceive(): boolean {
    return this.status === 'SHIPPED' || this.status === 'PARTIAL_RECEIVED';
  }

  get canForceComplete(): boolean {
    return this.status === 'PARTIAL_RECEIVED';
  }

  get canCancel(): boolean {
    return this.status === 'PENDING' || this.status === 'SHIPPED';
  }

  get canRevertToPending(): boolean {
    return this.status === 'SHIPPED';
  }

  // Status helper methods
  get isPartiallyReceived(): boolean {
    return this.status === 'PARTIAL_RECEIVED';
  }

  get isCompleted(): boolean {
    return this.status === 'COMPLETED';
  }

  get isPending(): boolean {
    return this.status === 'PENDING';
  }

  get isShipped(): boolean {
    return this.status === 'SHIPPED';
  }

  get isCancelled(): boolean {
    return this.status === 'CANCELLED';
  }

  get hasReceivingVariance(): boolean {
    return this.isPartialReceive || this.forceCompleted;
  }

  get receivingStatus(): string {
    if (this.forceCompleted) {
      return 'Force Completed';
    }
    if (this.isPartialReceive) {
      return 'Partially Received';
    }
    if (this.isCompleted) {
      return 'Fully Received';
    }
    return 'Not Received';
  }

  get receivedVariance(): number {
    if (this.totalExpectedQty > 0 && this.totalReceivedQty > 0) {
      return this.totalReceivedQty - this.totalExpectedQty;
    }
    return 0;
  }

  get receivedVariancePercentage(): number {
    if (this.totalExpectedQty > 0 && this.totalReceivedQty > 0) {
      return ((this.totalReceivedQty - this.totalExpectedQty) / this.totalExpectedQty) * 100;
    }
    return 0;
  }

  get editPermissionText(): string {
    if (this.canEditItems && this.canEditNote) {
      return 'Full Edit';
    }
    if (this.canEditNote) {
      return 'Notes Only';
    }
    return 'Read Only';
  }

  get editPermissionIcon(): string {
    if (this.canEditItems && this.canEditNote) {
      return 'mdi-pencil';
    }
    if (this.canEditNote) {
      return 'mdi-note-edit';
    }
    return 'mdi-lock';
  }

  get editPermissionColor(): string {
    if (this.canEditItems && this.canEditNote) {
      return 'green';
    }
    if (this.canEditNote) {
      return 'blue';
    }
    return 'grey';
  }

  // Legacy compatibility methods
  get canBeShipped(): boolean {
    return this.canShip;
  }

  get canBeReceived(): boolean {
    return this.canReceive;
  }

  // Alias for consistency with component expectations
  get destinationLocId(): number {
    return this.destLocId;
  }

  // Update method for the model - handles API call and self-update
  public async update(updateData: any): Promise<boolean> {
    try {
      // Import put dynamically to avoid circular dependency
      const { put } = await import('@/services/http');
      const response: any = await put(`/inventory-transfers/${this.id}`, updateData);

      if (response) {
        // Update current model instance with response data
        this.mapData(response);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating transfer:', error);
      return false;
    }
  }
}
